import {
  createWorkTag,
  deleteWorkTag,
  getWorkTagList,
  updateWorkTag,
} from '../../api/workTagApi';
import type { WorkTagData } from '../../dto/EntityRepository';
import { CreateTagReq, DeleteTagReq, UpdateTagReq } from '../../dto/ReqDtoRepository';
import { TagListResponse, TagResponse } from '../../dto/ResDtoRepository';
import { useModalWorkTagStore, useWorkTagStore } from '../../store/workTagStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { useModalStore } from '../../store/portal/modalStore';
import { useWorkViewStore } from '../../store/WorkViewStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import type { ResourceScope, ResourceStatus } from '../../util/scopedResource';

interface UseWorkTagStateResult {
  tags: WorkTagData[];
  selectedTags: WorkTagData[];
  untaggedSelected: boolean;
  listOwnerAui: string | null;
  listScope: ResourceScope | null;
  status: ResourceStatus;
}

interface UseWorkTagActionsResult {
  getWorkTagList: () => Promise<void>;
  createWorkTag: (data: CreateTagReq) => Promise<void>;
  updateWorkTag: (data: UpdateTagReq) => Promise<void>;
  deleteWorkTag: (data: DeleteTagReq) => Promise<void>;
  addSelectedTag: (tag: WorkTagData) => void;
  removeSelectedTag: (tag: WorkTagData) => void;
  selectUntagged: () => void;
  clearUntagged: () => void;
  clearSelectedTags: () => void;
  clearTags: () => void;
}

type UseWorkTagResult = UseWorkTagStateResult & UseWorkTagActionsResult;

export const useWorkTagState = (): UseWorkTagStateResult => {
  const tags = useWorkTagStore((state) => state.tags);
  const selectedTags = useWorkTagStore((state) => state.selectedTags);
  const untaggedSelected = useWorkTagStore((state) => state.untaggedSelected);
  const listOwnerAui = useWorkTagStore((state) => state.listOwnerAui);
  const listScope = useWorkTagStore((state) => state.listScope);
  const status = useWorkTagStore((state) => state.status);

  return { tags, selectedTags, untaggedSelected, listOwnerAui, listScope, status };
};

export const useWorkTagActions = (): UseWorkTagActionsResult => {
  const pageOwnerAui = usePageOwnerIdentityStore((s) => s.pageOwner?.aui);
  const setTags = useWorkTagStore((s) => s.setTags);
  const beginWorkTagListFetch = useWorkTagStore((s) => s.beginWorkTagListFetch);
  const setWorkTagListError = useWorkTagStore((s) => s.setWorkTagListError);
  const afterCreateTag = useWorkTagStore((s) => s.afterCreateTag);
  const afterUpdateTag = useWorkTagStore((s) => s.afterUpdateTag);
  const afterDeleteTag = useWorkTagStore((s) => s.afterDeleteTag);
  const addSelectedTag = useWorkTagStore((s) => s.addSelectedTag);
  const removeSelectedTag = useWorkTagStore((s) => s.removeSelectedTag);
  const selectUntagged = useWorkTagStore((s) => s.selectUntagged);
  const clearUntagged = useWorkTagStore((s) => s.clearUntagged);
  const clearSelectedTags = useWorkTagStore((s) => s.clearSelectedTags);
  const clearTags = useWorkTagStore((s) => s.clearTags);
  const { setUpdatedTempAlert, setDeletedTempAlert } = useTempAlertStore();
  const { clearModal } = useModalStore();
  const syncWorkViewTag = useWorkViewStore((s) => s.syncWorkTag);
  const removeWorkViewTag = useWorkViewStore((s) => s.removeWorkTag);
  const syncSelectedModalTag = useModalWorkTagStore((s) => s.syncSelectedModalTag);
  const removeSelectedModalTagById = useModalWorkTagStore(
    (s) => s.removeSelectedModalTagById,
  );
  const withApiHandler = useApiWrapper();

  const handleGetWorkTagListSuccess = (
    response: TagListResponse,
    ownerAui: string,
    scope: ResourceScope,
  ) => {
    setTags(response.data, ownerAui, scope);
  };

  const handleCreateWorkTagSuccess = (response: TagResponse) => {
    afterCreateTag(response.data);
    setUpdatedTempAlert();
    clearModal();
  };

  const handleUpdateWorkTagSuccess = (response: TagResponse) => {
    afterUpdateTag(response.data);
    syncWorkViewTag(response.data);
    syncSelectedModalTag(response.data);
    setUpdatedTempAlert();
  };

  const handleDeleteWorkTagSuccess = (tagId: number) => {
    afterDeleteTag(tagId);
    removeWorkViewTag(tagId);
    removeSelectedModalTagById(tagId);
    setDeletedTempAlert();
  };

  const fetchWorkTagList = async (aui: string) => {
    beginWorkTagListFetch(aui, 'public');
    try {
      return await runCurrentArchiveRequest(
        aui,
        () => getWorkTagList(aui),
        (response) => handleGetWorkTagListSuccess(response, aui, 'public'),
      );
    } catch (error) {
      setWorkTagListError();
      throw error;
    }
  };

  const handleWorkTagRequest = async (
    action: 'get' | 'create' | 'update' | 'delete',
    data?: CreateTagReq | UpdateTagReq | DeleteTagReq,
  ) => {
    if (!pageOwnerAui) return;
    const aui = pageOwnerAui;
    const apiFunction = async () => {
      switch (action) {
        case 'create':
          return runCurrentArchiveRequest(
            aui,
            () => createWorkTag(aui, data as CreateTagReq),
            handleCreateWorkTagSuccess,
          );
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateWorkTag(aui, data as UpdateTagReq),
            handleUpdateWorkTagSuccess,
          );
        case 'delete': {
          const tagId = (data as DeleteTagReq).tagId;
          return runCurrentArchiveRequest(
            aui,
            () => deleteWorkTag(aui, tagId),
            () => handleDeleteWorkTagSuccess(tagId),
          );
        }
        case 'get':
        default:
          return fetchWorkTagList(aui);
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };

  return {
    getWorkTagList: () => handleWorkTagRequest('get'),
    createWorkTag: (data: CreateTagReq) => handleWorkTagRequest('create', data),
    updateWorkTag: (data: UpdateTagReq) => handleWorkTagRequest('update', data),
    deleteWorkTag: (data: DeleteTagReq) => handleWorkTagRequest('delete', data),
    addSelectedTag,
    removeSelectedTag,
    selectUntagged,
    clearUntagged,
    clearSelectedTags,
    clearTags,
  };
};

/** 기존 호출부 호환: state + actions */
export const useWorkTag = (): UseWorkTagResult => ({
  ...useWorkTagState(),
  ...useWorkTagActions(),
});
